import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';

import { KubeJobsService } from '../services/kubejobs.service';
import {
  CronJobDetailDTO,
  CronJobSummaryDTO,
  TriggerJobResponse,
  emptyCronJobDetail,
  emptyCronJobSummaryList
} from '../services/models/KubeJobs';

// ── Actions ─────────────────────────────────────────────────────────

export class FetchCronJobsAction {
  static readonly type = '[KubeJobs] Fetch CronJobs';
}

export class FetchCronJobDetailAction {
  static readonly type = '[KubeJobs] Fetch CronJob Detail';
  constructor(public cronJobName: string) {}
}

export class TriggerCronJobAction {
  static readonly type = '[KubeJobs] Trigger CronJob';
  constructor(public cronJobName: string) {}
}

export class RestartCronJobAction {
  static readonly type = '[KubeJobs] Restart CronJob';
  constructor(public cronJobName: string) {}
}

export class SuspendCronJobAction {
  static readonly type = '[KubeJobs] Suspend CronJob';
  constructor(public cronJobName: string, public suspend: boolean) {}
}

export class UpdateCronJobScheduleAction {
  static readonly type = '[KubeJobs] Update CronJob Schedule';
  constructor(public cronJobName: string, public cronExpression: string) {}
}

export class KillCronJobAction {
  static readonly type = '[KubeJobs] Kill CronJob';
  constructor(public cronJobName: string) {}
}

export class ResetKubeJobsStateAction {
  static readonly type = '[KubeJobs] Reset State';
}

// ── State Model ─────────────────────────────────────────────────────

export interface KubeJobsStateModel {
  loading: boolean;
  cronJobs: CronJobSummaryDTO[];
  selectedCronJobDetail: CronJobDetailDTO;
  lastTriggerResponse: TriggerJobResponse;
}

const defaultState: KubeJobsStateModel = {
  loading: false,
  cronJobs: emptyCronJobSummaryList(),
  selectedCronJobDetail: emptyCronJobDetail(),
  lastTriggerResponse: null
};

const getDefaultState = (): KubeJobsStateModel => ({ ...defaultState });

// ── State ───────────────────────────────────────────────────────────

@State<KubeJobsStateModel>({
  name: 'kubeJobs',
  defaults: getDefaultState()
})
@Injectable()
export class KubeJobsState {
  private readonly kubeJobsService = inject(KubeJobsService);

  constructor() {}

  // ── Fetch all CronJobs ────────────────────────────────────────────

  @Action(FetchCronJobsAction)
  fetchCronJobs({ patchState }: StateContext<KubeJobsStateModel>) {
    patchState({ loading: true });
    return this.kubeJobsService.listCronJobs().pipe(
      tap((res) => {
        patchState({ cronJobs: res.data ?? [], loading: false });
      }),
      catchError((error) => {
        patchState(getDefaultState());
        throw error;
      })
    );
  }

  // ── Fetch CronJob detail ──────────────────────────────────────────

  @Action(FetchCronJobDetailAction)
  fetchCronJobDetail({ patchState }: StateContext<KubeJobsStateModel>, { cronJobName }: FetchCronJobDetailAction) {
    patchState({ loading: true });
    return this.kubeJobsService.getCronJobDetail(cronJobName).pipe(
      tap((res) => {
        patchState({ selectedCronJobDetail: res.data, loading: false });
      }),
      catchError((error) => {
        patchState({ selectedCronJobDetail: emptyCronJobDetail(), loading: false });
        throw error;
      })
    );
  }

  // ── Trigger a CronJob ─────────────────────────────────────────────

  @Action(TriggerCronJobAction)
  triggerCronJob({ patchState }: StateContext<KubeJobsStateModel>, { cronJobName }: TriggerCronJobAction) {
    patchState({ loading: true });
    return this.kubeJobsService.triggerJob(cronJobName).pipe(
      tap((res) => {
        patchState({ lastTriggerResponse: res.data, loading: false });
      }),
      catchError((error) => {
        patchState({ loading: false });
        throw error;
      })
    );
  }

  // ── Restart a CronJob ─────────────────────────────────────────────

  @Action(RestartCronJobAction)
  restartCronJob({ patchState }: StateContext<KubeJobsStateModel>, { cronJobName }: RestartCronJobAction) {
    patchState({ loading: true });
    return this.kubeJobsService.restartJob(cronJobName).pipe(
      tap((res) => {
        patchState({ lastTriggerResponse: res.data, loading: false });
      }),
      catchError((error) => {
        patchState({ loading: false });
        throw error;
      })
    );
  }

  // ── Suspend / Resume a CronJob ────────────────────────────────────

  @Action(SuspendCronJobAction)
  suspendCronJob({ patchState }: StateContext<KubeJobsStateModel>, { cronJobName, suspend }: SuspendCronJobAction) {
    patchState({ loading: true });
    return this.kubeJobsService.setSuspended(cronJobName, { suspend }).pipe(
      tap(() => {
        patchState({ loading: false });
      }),
      catchError((error) => {
        patchState({ loading: false });
        throw error;
      })
    );
  }

  // ── Update CronJob schedule ───────────────────────────────────────

  @Action(UpdateCronJobScheduleAction)
  updateCronJobSchedule({ patchState }: StateContext<KubeJobsStateModel>, { cronJobName, cronExpression }: UpdateCronJobScheduleAction) {
    patchState({ loading: true });
    return this.kubeJobsService.updateSchedule(cronJobName, { cronExpression }).pipe(
      tap(() => {
        patchState({ loading: false });
      }),
      catchError((error) => {
        patchState({ loading: false });
        throw error;
      })
    );
  }

  // ── Kill running jobs ─────────────────────────────────────────────

  @Action(KillCronJobAction)
  killCronJob({ patchState }: StateContext<KubeJobsStateModel>, { cronJobName }: KillCronJobAction) {
    patchState({ loading: true });
    return this.kubeJobsService.killJob(cronJobName).pipe(
      tap(() => {
        patchState({ loading: false });
      }),
      catchError((error) => {
        patchState({ loading: false });
        throw error;
      })
    );
  }

  // ── Reset ─────────────────────────────────────────────────────────

  @Action(ResetKubeJobsStateAction)
  resetState({ patchState }: StateContext<KubeJobsStateModel>) {
    patchState(getDefaultState());
  }

  // ── Selectors ─────────────────────────────────────────────────────

  @Selector()
  static state(state: KubeJobsStateModel) {
    return state;
  }

  @Selector()
  static loading(state: KubeJobsStateModel) {
    return state.loading;
  }

  @Selector()
  static cronJobs(state: KubeJobsStateModel) {
    return state.cronJobs;
  }

  @Selector()
  static selectedCronJobDetail(state: KubeJobsStateModel) {
    return state.selectedCronJobDetail;
  }

  @Selector()
  static lastTriggerResponse(state: KubeJobsStateModel) {
    return state.lastTriggerResponse;
  }
}
